import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteGuideline } from '../api/guidelines.api';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import { useTranslation } from 'react-i18next';

export interface DeleteGuidelineParams {
  guidelineId: string;
  guidelineName?: string;
  folderId?: string;
}

/**
 * Hook to delete a guideline using TanStack Query mutation
 *
 * @returns TanStack Query mutation result with delete guideline functionality
 */
export const useDeleteGuideline = () => {
  const { t } = useTranslation('knowledge');
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  return useMutation<undefined, Error, DeleteGuidelineParams>({
    mutationFn: async ({ guidelineId }: DeleteGuidelineParams) => {
      await deleteGuideline(guidelineId);
      return undefined;
    },
    onSuccess: (_, variables) => {
      // Invalidate folder guidelines query to refetch the list
      if (variables.folderId) {
        void queryClient.invalidateQueries({
          queryKey: ['guidelines', 'folder', variables.folderId],
        });
      }
      // Also invalidate the folders query for guideline counts
      void queryClient.invalidateQueries({ queryKey: ['folders', workspaceId] });

      const message = variables.guidelineName
        ? t('guidelines.useDeleteGuideline.guidelineDeleted', { name: variables.guidelineName })
        : t('guidelines.useDeleteGuideline.guidelineDeletedSuccessfully');

      notificationService.showSuccess(message, {
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      });
    },
    onError: (error) => {
      notificationService.showError(
        error.message || t('guidelines.useDeleteGuideline.failedToDeleteGuideline'),
      );
    },
  });
};
